import type { CollectionRow, Order } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatStamp, parseTyped, todayIso } from '../format';
import { useLocale } from '../i18n';
import { daysLeftLabel } from './CompliancePage';
import { qk } from '../queryClient';

/** Confirm drafts one after another; each refused one (a credit hold, no price) is kept with the server's reason. */
export async function confirmAll(orders: Order[], confirm: (id: string) => Promise<unknown>): Promise<{ done: number; failed: { order: Order; why: string }[] }> {
  let done = 0;
  const failed: { order: Order; why: string }[] = [];
  for (const o of orders) {
    try {
      await confirm(o.id);
      done += 1;
    } catch (err) {
      failed.push({ order: o, why: err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : String(err) });
    }
  }
  return { done, failed };
}

/** The office's worklists (decision 108): drafts to confirm, money to chase, complaints open, products with no cost. */
export function WorklistsPage() {
  const { t } = useLocale();
  return (
    <div className="stack">
      <PageHead title={t('worklistsTitle')} back="/" />
      <Drafts />
      <Collections />
      <Complaints />
      <NoCost />
      <RenewalsDue />
    </div>
  );
}

function Drafts() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['orders', 'drafts'], queryFn: ({ signal }) => api.ordersByStatus('draft', signal) });
  const [result, setResult] = useState<Awaited<ReturnType<typeof confirmAll>> | null>(null);
  const run = useMutation({
    mutationFn: (orders: Order[]) => confirmAll(orders, (id) => api.setOrderStatus(id, 'confirmed')),
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
  return (
    <section className="card stack-sm" data-testid="drafts">
      <h2>{t('draftsToConfirm')}</h2>
      <QueryView query={query}>
        {(all) => {
          // a draft for a day already gone is not confirmed in bulk: it would land on a pick list for nobody
          const today = todayIso();
          const orders = all.filter((o) => o.requestedDate >= today);
          const stale = all.filter((o) => o.requestedDate < today);
          return all.length === 0 ? (
            <Empty>{t('noDrafts')}</Empty>
          ) : (
            <>
              {stale.length > 0 ? (
                <details className="note" data-testid="stale-drafts">
                  <summary>
                    {stale.length} {t('staleDrafts')}
                  </summary>
                  <ul className="list">
                    {stale.map((o) => (
                      <li key={o.id}>
                        <Link className="row" to={`/orders/${o.id}`}>
                          <span className="row-main">
                            <span className="row-title">
                              #{o.orderNo} · {o.customerName}
                            </span>
                            <span className="row-sub">{formatDate(o.requestedDate, intlLocale, 'date')}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              <ul className="list">
                {orders.map((o) => (
                  <li key={o.id}>
                    <Link className="row" to={`/orders/${o.id}`}>
                      <span className="row-main">
                        <span className="row-title">
                          #{o.orderNo} · {o.customerName}
                        </span>
                        <span className="row-sub">
                          {formatDate(o.requestedDate, intlLocale, 'date')} · {t(`channel_${o.channel}`)}
                        </span>
                      </span>
                      <span className="row-end-value">{formatMoney(o.total, intlLocale)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              {orders.length > 0 ? (
                <button type="button" className="btn btn-primary btn-block" disabled={run.isPending} onClick={() => window.confirm(t('confirmAllAsk').replace('{n}', String(orders.length))) && run.mutate(orders)}>
                  {t('confirmAll')} ({orders.length})
                </button>
              ) : null}
            </>
          );
        }}
      </QueryView>
      {result ? (
        <div className="flash" role="status" data-testid="confirm-result">
          ✓ {result.done} {t('confirmedCount')}
          {result.failed.map((f) => (
            <div key={f.order.id} className="field-error">
              #{f.order.orderNo} {f.order.customerName}: {f.why}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function Collections() {
  const { t } = useLocale();
  const query = useQuery({ queryKey: ['work', 'collections'], queryFn: ({ signal }) => api.collections(signal) });
  return (
    <section className="card wide stack-sm" data-testid="collections">
      <h2>{t('collectionsTitle')}</h2>
      <QueryView query={query}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('nothingPastDue')}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('customer')}</th>
                    <th className="num">{t('pastDueCol')}</th>
                    <th className="num">{t('balance')}</th>
                    <th>{t('lastPaid')}</th>
                    <th>{t('lastCall')}</th>
                    <th>{t('promiseCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <CollectionLine key={r.customerId} row={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryView>
      <p className="muted">{t('collectionsHint')}</p>
    </section>
  );
}

function CollectionLine({ row: r }: { row: CollectionRow }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(r.pastDue));
  const [on, setOn] = useState(addDays(todayIso(), 7));
  const [key, setKey] = useState(() => crypto.randomUUID());
  const n = parseTyped(amount);
  const save = useMutation({
    mutationFn: () => api.addPromise(r.customerId, { amount: Math.round((n ?? 0) * 100) / 100, promisedOn: on }, key),
    onSuccess: () => {
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['work', 'collections'] });
    },
  });
  const p = r.promise;
  return (
    <tr data-testid="collection">
      <td>
        <Link to={`/customers/${r.customerId}/statement`}>{r.customerName}</Link>
        {r.onHold ? <span className="badge badge-danger">{t('onHold')}</span> : null}
        {/* put away with money after (decision 184): bring them back on their page, then collect */}
        {r.archived ? (
          <Link className="badge badge-warn" to={`/customers/${r.customerId}`}>
            {t('archivedFilter')}
          </Link>
        ) : null}
      </td>
      <td className="num owed">
        {formatMoney(r.pastDue, intlLocale)} <span className="muted">· {r.daysLate} {t('daysLate')}</span>
      </td>
      <td className="num">{formatMoney(r.balance, intlLocale)}</td>
      <td>{r.lastPaidAt ? formatStamp(r.lastPaidAt, intlLocale, 'date') : '—'}</td>
      <td>
        {r.lastContactAt ? formatStamp(r.lastContactAt, intlLocale, 'date') : '—'}{' '}
        <Link to={`/customers/${r.customerId}`} className="muted">
          {t('logCall')}
        </Link>
      </td>
      <td>
        {p ? (
          <span className={p.status === 'broken' ? 'badge badge-danger' : p.status === 'kept' ? 'badge badge-ok' : 'badge'} data-testid="promise">
            {formatMoney(p.amount, intlLocale)} · {formatDate(p.promisedOn, intlLocale, 'date')} · {t(`promise_${p.status}`)}
          </span>
        ) : null}
        {open ? (
          <form
            className="contact-actions"
            onSubmit={(e) => {
              e.preventDefault();
              if (n !== null && n > 0) save.mutate();
            }}
          >
            <input type="text" inputMode="decimal" value={amount} aria-label={`${t('promiseAmount')} ${r.customerName}`} aria-invalid={n === null || n <= 0 || undefined} onChange={(e) => setAmount(e.target.value)} style={{ width: 90 }} />
            <input type="date" value={on} min={todayIso()} max={addDays(todayIso(), 180)} aria-label={`${t('promiseBy')} ${r.customerName}`} onChange={(e) => setOn(e.target.value)} />
            <button type="submit" className="btn btn-secondary" disabled={save.isPending || n === null || n <= 0}>
              {t('save')}
            </button>
          </form>
        ) : r.archived ? null : (
          // one put away is brought back on their page first (the Archived badge links there)
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setKey(crypto.randomUUID());
              setOpen(true);
            }}
          >
            {t('addPromise')}
          </button>
        )}
        {save.error ? <ErrorBox error={save.error} /> : null}
      </td>
    </tr>
  );
}

function Complaints() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['work', 'complaints'], queryFn: ({ signal }) => api.complaints(signal) });
  const done = useMutation({ mutationFn: (id: string) => api.setActivityDone(id, true), onSuccess: () => void qc.invalidateQueries({ queryKey: ['work', 'complaints'] }) });
  return (
    <section className="card stack-sm" data-testid="complaints">
      <h2>{t('complaintsTitle')}</h2>
      <p className="muted">{t('complaintsHint')}</p>
      <QueryView query={query}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noComplaints')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((c) => (
                <li key={c.id} className={c.doneAt ? 'row muted' : 'row'} data-testid="complaint">
                  <span className="row-main">
                    <span className="row-title">
                      <Link to={`/customers/${c.customerId}`}>{c.customerName}</Link> · {c.body}
                    </span>
                    <span className="row-sub">
                      {formatStamp(c.happenedAt, intlLocale, 'date')}
                      {c.byName ? ` · ${c.byName}` : ''}
                      {c.doneAt ? ` · ${t('resolved')} ${formatStamp(c.doneAt, intlLocale, 'date')}` : ''}
                    </span>
                  </span>
                  {c.doneAt ? null : (
                    <button type="button" className="btn btn-secondary" disabled={done.isPending} onClick={() => done.mutate(c.id)}>
                      {t('markResolved')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
      {done.error ? <ErrorBox error={done.error} /> : null}
    </section>
  );
}

function NoCost() {
  const { t, intlLocale } = useLocale();
  const query = useQuery({ queryKey: ['work', 'no-cost'], queryFn: ({ signal }) => api.noCost(signal) });
  return (
    <section className="card stack-sm" data-testid="no-cost">
      <h2>{t('noCostTitle')}</h2>
      <p className="muted">{t('noCostHint')}</p>
      <QueryView query={query}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((r) => (
                <li key={r.itemId}>
                  <Link className="row" to={`/items/${r.itemId}`}>
                    <span className="row-main">
                      <span className="row-title">{r.itemName}</span>
                      <span className="row-sub">
                        {r.sku ?? ''} {r.lines} {t('linesSold')} · {formatDate(r.lastSold, intlLocale, 'date')}
                      </span>
                    </span>
                    <span className="row-end-value">{formatMoney(r.sales, intlLocale)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </section>
  );
}

/** Licenses, permits, policies and registrations running out (decision 112); the list itself is on Compliance. */
function RenewalsDue() {
  const { t, intlLocale } = useLocale();
  const query = useQuery({ queryKey: qk.renewals, queryFn: ({ signal }) => api.renewals(signal) });
  const due = (query.data ?? []).filter((r) => r.status !== 'ok');
  if (due.length === 0) return null;
  return (
    <section className="card stack-sm" data-testid="renewals-due">
      <h2>{t('renewalsTitle')}</h2>
      <ul className="list">
        {due.map((r) => (
          <li key={r.id}>
            <Link className="row" to="/compliance">
              <span className="row-main">
                <span className="row-title">
                  {r.name}
                  {r.holder ? ` · ${r.holder}` : ''}
                </span>
                <span className="row-sub">{formatDate(r.expiresOn, intlLocale, 'date')}</span>
              </span>
              <span className={r.status === 'expired' ? 'badge badge-danger' : 'badge badge-warn'}>{daysLeftLabel(r, t)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
